/* eslint-disable max-depth */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable camelcase */
/* eslint-disable security/detect-object-injection */
import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  AddCircle as AddCircleIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material'
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  RaRecord,
} from 'react-admin'
import { xxllncColor } from '../../layout/themes'
import { StyledBox, classes } from './ActionFormStyles'
import {
  STATUS_OPTIONS,
  STATUS_LABELS,
  STATUS_SHORT,
  channelIcons,
  channelLabels,
  HANDLING_STATUS,
  QuestionData,
  Employee,
  ActionFormProps,
  HandlingStatus,
  STATUS_TO_CASE_TYPE,
} from './ActionFormConstants'
import { StatusIcon } from './StatusIcon'
import { CaseLinkSection, Case } from './CaseLinkSection'

interface OverrideActionFormProps extends Omit<ActionFormProps, 'onLinkCase'> {
  onLinkCase: (caseUuids: string[]) => void;
}

interface ExtendedActionFormProps extends OverrideActionFormProps {
  onHandlingTypeChange?: (type: HandlingStatus) => void;
  caseUuids?: string[];
  handlingType?: HandlingStatus;
  onSave: (data: {
    questions: QuestionData[];
    status: string;
    handlingType: HandlingStatus;
    employeeSearch?: string;
    employee_uuid?: string;
    case_type_uuid?: string;
    subject?: string;
    caseUuid?: string | null;
    case_uuids?: string[];
    case_uuids_authoritative?: boolean;
  }) => void;
}

const ActionForm = ({
  channel,
  questions: initialQuestions,
  status,
  caseUuid,
  caseUuids: initialCaseUuids,
  handlingType: initialHandlingType,
  onSave,
  onQuestionsChange,
  onLinkCase,
  onHandlingTypeChange,
}: ExtendedActionFormProps) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const currentRecord = useRecordContext<RaRecord>()

  const [questions, setQuestions] = useState<QuestionData[]>(
    initialQuestions.length > 0
      ? initialQuestions
      : [{ text: '', notes: '', note_uuid: null }],
  )
  const [activeQuestion, setActiveQuestion] = useState(0)
  const [handlingTypes, setHandlingTypes] = useState<HandlingStatus[]>(
    initialQuestions.length > 0
      ? initialQuestions.map(
        () => initialHandlingType || HANDLING_STATUS.ANSWERED,
      )
      : [initialHandlingType || HANDLING_STATUS.ANSWERED],
  )
  const handlingType =
    handlingTypes[activeQuestion] ?? HANDLING_STATUS.ANSWERED
  const [employeeSearch, setEmployeeSearch] = useState('')
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  )
  const [employeeResults, setEmployeeResults] = useState<Employee[]>([])
  const [showEmployeeResults, setShowEmployeeResults] = useState(false)

  const [isSaved, setIsSaved] = useState(status === 'completed')

  const [caseSearch, setCaseSearch] = useState('')
  const [caseResults, setCaseResults] = useState<Case[]>([])
  const [showCaseResults, setShowCaseResults] = useState(false)

  const [internalCaseUuids, setInternalCaseUuids] = useState<string[]>(
    initialCaseUuids || (caseUuid ? [caseUuid] : []),
  )
  const [selectedCasesData, setSelectedCasesData] = useState<Case[]>([])

  const isLinkingRef = useRef(false)
  const isResettingRef = useRef(false)
  const effectiefHoofdzaakRef = useRef<string | null>(caseUuid || null)
  const isFirstRender = useRef(true)
  const hasLoadedData = useRef(false)

  useEffect(() => {
    effectiefHoofdzaakRef.current = caseUuid || null
  }, [caseUuid])

  useEffect(() => {
    if (currentRecord) {
      setIsSaved(status === 'completed')
    }
  }, [currentRecord?.id, status])
  useEffect(() => {
    if (initialCaseUuids && !isLinkingRef.current) {
      setInternalCaseUuids(initialCaseUuids)
    }
  }, [initialCaseUuids])

  useEffect(() => {
    if (currentRecord) {
      const savedEmployeeUuid =
        (currentRecord.employee_uuid as string | undefined) ||
        initialQuestions[0]?.note_uuid
      if (savedEmployeeUuid) {
        setSelectedEmployeeId(String(savedEmployeeUuid))
        dataProvider
          .getOne<Employee>('employee', { id: String(savedEmployeeUuid) })
          .then(({ data }) => {
            if (data) setEmployeeSearch(data.name)
          })
          .catch((err) =>
            console.error('Kon medewerkernaam niet herstellen:', err),
          )
      } else {
        setSelectedEmployeeId(null)
        setEmployeeSearch('')
      }
    }
  }, [currentRecord?.id, dataProvider])

  useEffect(() => {
    if (hasLoadedData.current) return

    if (
      initialQuestions &&
      initialQuestions.length > 0 &&
      initialQuestions[0].text !== ''
    ) {
      setQuestions(initialQuestions)
      setHandlingTypes(
        initialQuestions.map(
          (_, i) => handlingTypes[i] || initialHandlingType || 'answered',
        ),
      )
      hasLoadedData.current = true
    } else if (currentRecord && currentRecord.subject) {
      setQuestions([
        {
          text: String(currentRecord.subject),
          notes: String(currentRecord.notes || ''),
          note_uuid: currentRecord.employee_uuid
            ? String(currentRecord.employee_uuid)
            : null,
        } as QuestionData,
      ])
      hasLoadedData.current = true
    }
  }, [initialQuestions, currentRecord, currentRecord?.subject])

  useEffect(() => {
    if (isLinkingRef.current) return

    if (internalCaseUuids.length === 0) {
      setSelectedCasesData([])
      return
    }

    const fetchAllCaseDetails = async () => {
      try {
        const uniekeUuids = Array.from(new Set(internalCaseUuids))
        const nieuweKaarten: Case[] = [...selectedCasesData]

        for (const uuid of uniekeUuids) {
          if (currentRecord && uuid === currentRecord.id) continue

          const reedsGeladen = nieuweKaarten.find((c) => c.id === uuid)
          if (
            reedsGeladen &&
            (reedsGeladen.case_number || reedsGeladen.subject)
          )
            continue

          try {
            const { data } = await dataProvider.getOne<Case>('case', {
              id: uuid,
            })
            if (!data) continue

            const rawData = data as unknown as Record<string, unknown>
            let caseNum = rawData.case_number || rawData.caseNumber || ''
            let caseSub =
              rawData.subject || rawData.title || rawData.description || ''

            const cacheKey = `case_cache_${uuid}`
            const localCacheStr = localStorage.getItem(cacheKey)
            if (localCacheStr) {
              try {
                const cachedObj = JSON.parse(localCacheStr) as Record<
                  string,
                  unknown
                >
                if (!caseNum && cachedObj.case_number)
                  caseNum = String(cachedObj.case_number)
                if (!caseSub && cachedObj.subject)
                  caseSub = String(cachedObj.subject)
              } catch (_) {
                /* negeer parse errors */
              }
            }

            const mooieTitel = caseNum
              ? `Zaak #${String(caseNum)}`
              : `Zaak #${String(data.id).substring(0, 8)}...`

            const nieuweKaart: Case = {
              ...data,
              title: mooieTitel,
              subject: caseSub ? String(caseSub) : mooieTitel,
              case_number: caseNum ? String(caseNum) : String(data.id || ''),
            } as Case

            const idx = nieuweKaarten.findIndex((item) => item.id === uuid)
            if (idx > -1) nieuweKaarten[idx] = nieuweKaart
            else nieuweKaarten.push(nieuweKaart)
          } catch (fetchErr) {
            console.error(
              `Kon details voor zaak ${uuid} niet ophalen:`,
              fetchErr,
            )
          }
        }

        setSelectedCasesData(
          nieuweKaarten.filter((c) => uniekeUuids.includes(c.id)),
        )
      } catch (e) {
        console.error('Ophalen van kaartdetails mislukt:', e)
      }
    }
    void fetchAllCaseDetails()
  }, [internalCaseUuids, dataProvider, currentRecord])

  useEffect(() => {
    if (caseSearch.trim() === '') {
      setShowCaseResults(false)
      setCaseResults([])
    }
  }, [caseSearch])

  useEffect(() => {
    if (!isSaved && !isLinkingRef.current) {
      const timeoutId = setTimeout(() => {

        if (isResettingRef.current) {
          return
        }
        onQuestionsChange(questions)
      }, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [questions, isSaved, onQuestionsChange])

  const handleStatusToggleClick = (newStatus: HandlingStatus) => {
    const updated = [...handlingTypes]
    updated[activeQuestion] = newStatus
    setHandlingTypes(updated)

    if (newStatus === 'callback') {
      isResettingRef.current = true

      const updatedQuestions = questions.map(
        (q: QuestionData, idx): QuestionData =>
          idx === activeQuestion ? { ...q, notes: '' } : q,
      )
      setQuestions(updatedQuestions)
      onQuestionsChange(updatedQuestions)

      setTimeout(() => {
        isResettingRef.current = false
      }, 600)
    }

    if (onHandlingTypeChange) onHandlingTypeChange(newStatus)
  }

  const isDisabled = isSaved
  const currentQ = questions[activeQuestion] || {
    text: '',
    notes: '',
    note_uuid: null,
  }

  const updateCurrentQuestion = (field: 'text' | 'notes', value: string) => {
    const updated = [...questions]
    updated[activeQuestion] = { ...updated[activeQuestion], [field]: value }
    setQuestions(updated)
  }

  const handleCaseSearch = async () => {
    if (!caseSearch.trim()) return
    try {
      const { data } = await dataProvider.getList<Case>('case', {
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { keyword: caseSearch },
      })
      data.forEach((c) => {
        const rawC = c as unknown as Record<string, unknown>
        localStorage.setItem(
          `case_cache_${c.id}`,
          JSON.stringify({
            case_number: rawC.case_number || '',
            subject: rawC.subject || rawC.title || '',
          }),
        )
      })
      setCaseResults(data)
      setShowCaseResults(true)
    } catch (e) {
      console.error('Case search failed:', e)
    }
  }

  const handleSelectCase = (c: Case) => {
    isLinkingRef.current = true
    setShowCaseResults(false)
    setCaseSearch('')

    const rawC = c as unknown as Record<string, unknown>
    const caseNum = rawC.case_number || rawC.caseNumber || ''
    const caseSub = rawC.subject || rawC.title || rawC.description || ''

    localStorage.setItem(
      `case_cache_${c.id}`,
      JSON.stringify({
        case_number: caseNum || '',
        subject: caseSub || '',
      }),
    )

    const mooieTitel = caseNum
      ? `Zaak #${String(caseNum)}`
      : `Zaak #${String(c.id).substring(0, 8)}...`

    const gemaakteKaart: Case = {
      ...c,
      title: mooieTitel,
      subject: caseSub ? String(caseSub) : mooieTitel,
      case_number: caseNum ? String(caseNum) : String(c.id),
    } as Case

    setSelectedCasesData((prev) => [
      ...prev.filter((item) => item.id !== c.id),
      gemaakteKaart,
    ])

    const updatedUuids = [...internalCaseUuids, c.id]
    setInternalCaseUuids(updatedUuids)

    if (typeof onLinkCase === 'function') onLinkCase(updatedUuids)

    notify('Zaak gekoppeld', { type: 'success' })
    setTimeout(() => {
      isLinkingRef.current = false
    }, 800)
  }

  const handleUnlinkCase = async (targetId?: string) => {
    isLinkingRef.current = true

    if (typeof targetId === 'string') {
      localStorage.removeItem(`case_cache_${targetId}`)

      const updatedUuids = internalCaseUuids.filter((id) => id !== targetId)
      setInternalCaseUuids(updatedUuids)
      setSelectedCasesData((prev) => prev.filter((c) => c.id !== targetId))

      if (typeof onLinkCase === 'function') onLinkCase(updatedUuids)

      if (currentRecord?.id && effectiefHoofdzaakRef.current) {
        try {
          await dataProvider.delete('conversation', {
            id: `${String(currentRecord.id)}/relation/${targetId}`,
            previousData: { id: '' },
          })
          notify('Zaak losgekoppeld', { type: 'info' })
        } catch (e) {
          console.error('Relatie verwijderen in zaaksysteem mislukt:', e)
          notify('Zaak losgekoppeld (zaaksysteem sync mislukt)', {
            type: 'warning',
          })
        }
      } else {
        notify('Zaak losgekoppeld', { type: 'info' })
      }
    } else {
      internalCaseUuids.forEach((id) =>
        localStorage.removeItem(`case_cache_${id}`),
      )
      setInternalCaseUuids([])
      setSelectedCasesData([])
      setCaseSearch('')
      if (typeof onLinkCase === 'function') onLinkCase([])
      notify('Alle koppelingen verwijderd', { type: 'info' })
    }

    setTimeout(() => {
      isLinkingRef.current = false
    }, 800)
  }

  const handleEmployeeSearch = async (value: string) => {
    setEmployeeSearch(value)
    if (value.length < 2) {
      setEmployeeResults([])
      setShowEmployeeResults(false)
      return
    }
    try {
      const { data } = await dataProvider.getList<Employee>('employee', {
        pagination: { page: 1, perPage: 10 },
        sort: { field: 'name', order: 'ASC' },
        filter: { keyword: value.trim() },
      })
      setEmployeeResults(data || [])
      setShowEmployeeResults((data || []).length > 0)
    } catch (e) {
      console.error(e)
    }
  }

  const handleSave = () => {
    const hasAnyContent = questions.some((q) => q.text.trim())
    if (!hasAnyContent) {
      notify('Vul minimaal één klantvraag in.', { type: 'error' })
      return
    }

    const updatedQuestions = [...questions]
    if (updatedQuestions.length > 0 && selectedEmployeeId) {
      updatedQuestions[0] = {
        ...updatedQuestions[0],
        note_uuid: selectedEmployeeId,
      }
    }
    const hoofdHandlingType = handlingTypes[0] ?? HANDLING_STATUS.ANSWERED

    const actueleUuids = [...internalCaseUuids]
    const singleCaseUuidString =
      actueleUuids.length > 0 ? String(actueleUuids[0]) : null

    if (currentRecord && currentRecord.id) {
      onSave({
        questions: updatedQuestions,
        status: 'completed',
        handlingType: hoofdHandlingType,
        employeeSearch: employeeSearch || undefined,
        employee_uuid: selectedEmployeeId ?? undefined,
        case_type_uuid: STATUS_TO_CASE_TYPE[hoofdHandlingType] ?? undefined,
        subject: STATUS_LABELS[hoofdHandlingType],
        caseUuid: singleCaseUuidString,
        case_uuids: actueleUuids,
        case_uuids_authoritative: true,
      })
      setIsSaved(true)
    }
  }

  const handleCancel = () => {
    setQuestions([{ text: '', notes: '', note_uuid: null }])
    setActiveQuestion(0)
    setHandlingTypes([initialHandlingType || HANDLING_STATUS.ANSWERED])
    setEmployeeSearch('')
    setSelectedEmployeeId(null)
    setCaseSearch('')
    setShowCaseResults(false)
    setInternalCaseUuids([])
    setSelectedCasesData([])
    if (typeof onLinkCase === 'function') onLinkCase([])
  }

  return (
    <StyledBox>
      <Paper className={classes.card}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {channelIcons[channel] || null}
          <Typography sx={{ fontWeight: 'bold' }}>
            {channelLabels[channel] || channel}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.5 }}>
          <Typography variant="body2" sx={{ mr: 0.5, color: 'black' }}>
            Klantvraag
          </Typography>
          {questions.map((_, idx) => (
            <Box
              key={idx}
              className={`${classes.questionTab} ${
                idx === activeQuestion ? classes.questionTabActive : ''
              }`}
              onClick={() => {
                if (!isDisabled) setActiveQuestion(idx)
              }}
              sx={{ cursor: isDisabled ? 'default' : 'pointer' }}
            >
              {idx + 1}
            </Box>
          ))}
          {!isDisabled && questions.length < 5 && (
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={() => {
                const newQuestions = [
                  ...questions,
                  { text: '', notes: '', note_uuid: null },
                ]
                setQuestions(newQuestions)
                setHandlingTypes([...handlingTypes, HANDLING_STATUS.ANSWERED])
                setActiveQuestion(newQuestions.length - 1)
              }}
            >
              <AddCircleIcon sx={{ color: xxllncColor.veniceBlue }} />
            </IconButton>
          )}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          value={currentQ.text}
          onChange={(e) => updateCurrentQuestion('text', e.target.value)}
          disabled={isDisabled}
          placeholder="Beschrijf de klantvraag..."
          error={currentQ.text.length > 500}
          sx={{
            'mb': 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              bgcolor: isDisabled ? '#fff' : 'white',
            },
          }}
        />
        {!isDisabled && (
          <Typography
            variant="caption"
            color={currentQ.text.length > 500 ? 'error' : 'textSecondary'}
          >
            Nog ruimte voor {500 - currentQ.text.length} karakters
          </Typography>
        )}

        <CaseLinkSection
          caseSearch={caseSearch}
          setCaseSearch={setCaseSearch}
          handleCaseSearch={handleCaseSearch}
          caseResults={caseResults}
          showCaseResults={showCaseResults}
          internalCaseUuids={internalCaseUuids}
          handleSelectCase={handleSelectCase}
          isDisabled={isDisabled}
          onUnlinkCase={(id) => {
            void handleUnlinkCase(id)
          }}
          selectedCases={selectedCasesData}
          caseUuid={caseUuid}
        />
      </Paper>

      <Paper className={classes.card} sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentTurnedInIcon fontSize="small" />
            <Typography sx={{ fontWeight: 'bold' }}>Afhandeling</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {STATUS_LABELS[handlingType]}
            </Typography>
            <StatusIcon type={handlingType} />
          </Box>
        </Box>

        <Box
          className={classes.statusToggle}
          sx={{ opacity: isDisabled ? 0.6 : 1 }}
        >
          {STATUS_OPTIONS.map((st) => (
            <Button
              key={st}
              fullWidth
              size="small"
              disabled={isDisabled}
              onClick={() => handleStatusToggleClick(st)}
              className={`${classes.statusButton} ${
                handlingType === st ? classes.statusButtonActive : ''
              }`}
            >
              {STATUS_SHORT[st]}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {(handlingType === 'callback' || handlingType === 'forwarded') && (
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Medewerker of afdeling"
                value={employeeSearch}
                autoComplete="off"
                onChange={(e) => handleEmployeeSearch(e.target.value)}
                onFocus={() => {
                  if (employeeResults.length > 0) setShowEmployeeResults(true)
                }}
                onBlur={() =>
                  setTimeout(() => setShowEmployeeResults(false), 200)
                }
                disabled={isDisabled}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: xxllncColor.mineShaft }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    bgcolor: isDisabled ? '#fff' : 'white',
                  },
                }}
              />
              {showEmployeeResults && (
                <Paper className={classes.medewerkerDropdown}>
                  <Paper>
                    <List dense>
                      {employeeResults.map((emp) => (
                        <ListItem
                          key={emp.id}
                          onClick={() => {
                            setEmployeeSearch(emp.name)
                            setSelectedEmployeeId(emp.id)
                            setShowEmployeeResults(false)
                          }}
                          sx={{
                            'cursor': 'pointer',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                          }}
                        >
                          <ListItemText
                            primary={emp.name}
                            secondary={emp.department}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Paper>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={handlingType === 'answered' ? 5 : 3}
            placeholder="Notitie"
            value={currentQ.notes}
            onChange={(e) => updateCurrentQuestion('notes', e.target.value)}
            disabled={isDisabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                bgcolor: isDisabled ? '#fff' : 'white',
              },
            }}
          />
          {!isDisabled && (
            <Typography
              variant="caption"
              color={currentQ.notes.length > 1200 ? 'error' : 'textSecondary'}
            >
              Nog ruimte voor {1200 - currentQ.notes.length} karakters
            </Typography>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        {!isSaved ? (
          <>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleCancel}
              className={classes.actionButton}
              sx={{
                color: xxllncColor.veniceBlue,
                borderColor: xxllncColor.veniceBlue,
              }}
            >
              Annuleer
            </Button>
            <Button
              variant="contained"
              endIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              onClick={handleSave}
              className={classes.actionButton}
              sx={{ bgcolor: xxllncColor.veniceBlue, color: '#fff' }}
            >
              Opslaan
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              disabled
              className={classes.actionButton}
              sx={{
                bgcolor: '#fff !important',
                color: `${xxllncColor.dark60} !important`,
              }}
            >
              Opgeslagen
            </Button>
            <Button
              variant="contained"
              endIcon={
                <ReplayIcon sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />
              }
              onClick={async () => {
                if (currentRecord) {
                  try {
                    await dataProvider.update('conversation', {
                      id: currentRecord.id,
                      data: {
                        ...currentRecord,
                        status: 'active',
                        case_uuids: internalCaseUuids,
                      },
                      previousData: currentRecord,
                    })
                    setIsSaved(false)
                    notify('Gesprek succesvol heropend', { type: 'info' })
                    window.location.reload()
                  } catch (err) {
                    console.error('Heropenen mislukt in API:', err)
                    notify('Heropenen mislukt', { type: 'error' })
                  }
                }
              }}
              className={classes.actionButton}
              sx={{
                'bgcolor': xxllncColor.veniceBlue,
                'color': '#fff',
                '&:hover': { bgcolor: xxllncColor.veniceBlue },
              }}
            >
              Heropenen
            </Button>
          </>
        )}
      </Box>
    </StyledBox>
  )
}

export default ActionForm
